import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { createRoot, Root } from 'react-dom/client';
import * as React from 'react';

const DashboardFilterPanel = require('@react/components/dashboard-filters/DashboardFilterPanel').default;

export interface DashboardFilterOption {
  key: string;
  value: string;
}

export interface DashboardFilterDateRange {
  fromDate: Date;
  toDate: Date;
}

/**
 * Angular host for the React StreamDASH filter panel.
 *
 * Angular keeps ownership of option loading, FilterService updates, routing and
 * the dashboard widgets. React only owns the filter UI; every interaction is
 * emitted back through an Angular output so the existing services stay the
 * source of truth.
 */
@Component({
  selector: 'app-dashboard-filter-bridge',
  template: '<div class="dashboard-filter-root"></div>',
  styles: [
    `
      :host {
        display: block;
      }

      .dashboard-filter-root {
        display: block;
      }
    `,
  ],
})
export class DashboardFilterBridgeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() viewMode = 'taiao';
  @Input() viewModeOptions: DashboardFilterOption[] = [];

  @Input() fromDate: Date | null = null;
  @Input() toDate: Date | null = null;

  @Input() contributingOrganisations: DashboardFilterOption[] = [];
  @Input() selectedContributingOrganisations: string[] = [];

  @Input() projects: DashboardFilterOption[] = [];
  @Input() selectedProjects: string[] = [];

  @Input() locations: DashboardFilterOption[] = [];
  @Input() selectedLocations: string[] = [];

  @Input() monitoringGroups: DashboardFilterOption[] = [];
  @Input() selectedMonitoringGroups: string[] = [];

  @Input() monitoringLocations: DashboardFilterOption[] = [];
  @Input() selectedMonitoringLocations: string[] = [];

  @Input() searchTerm = '';

  @Input() showTimescale = true;
  @Input() showSearch = false;
  @Input() showContributingOrganisations = false;
  @Input() showProjects = false;
  @Input() showLocations = false;
  @Input() showMonitoringGroups = false;
  @Input() showMonitoringLocations = false;
  @Input() showDownloadObservations = false;

  @Input() applyMonitoringDisabled = true;
  @Input() applyingMonitoringFilters = false;

  @Output() viewModeChange = new EventEmitter<string>();
  @Output() dateRangeChange = new EventEmitter<DashboardFilterDateRange>();
  @Output() contributingOrganisationsChange = new EventEmitter<string[]>();
  @Output() projectsChange = new EventEmitter<string[]>();
  @Output() locationsChange = new EventEmitter<string[]>();
  @Output() monitoringGroupsChange = new EventEmitter<string[]>();
  @Output() monitoringLocationsChange = new EventEmitter<string[]>();
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() applyMonitoringFilters = new EventEmitter<void>();
  @Output() downloadObservations = new EventEmitter<void>();

  private reactRoot: Root | null = null;

  constructor(private el: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    const container = this.el.nativeElement.querySelector('.dashboard-filter-root');
    if (!container) return;

    this.reactRoot = createRoot(container);
    this.renderPanel();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.renderPanel();
  }

  ngOnDestroy(): void {
    this.reactRoot?.unmount();
    this.reactRoot = null;
  }

  private runInZone(action: () => void): void {
    this.ngZone.run(action);
  }

  private renderPanel(): void {
    if (!this.reactRoot) return;

    this.reactRoot.render(
      React.createElement(DashboardFilterPanel, {
        viewMode: this.viewMode,
        viewModeOptions: this.viewModeOptions || [],
        fromDate: this.fromDate,
        toDate: this.toDate,
        contributingOrganisations: this.contributingOrganisations || [],
        selectedContributingOrganisations: this.selectedContributingOrganisations || [],
        projects: this.projects || [],
        selectedProjects: this.selectedProjects || [],
        locations: this.locations || [],
        selectedLocations: this.selectedLocations || [],
        monitoringGroups: this.monitoringGroups || [],
        selectedMonitoringGroups: this.selectedMonitoringGroups || [],
        monitoringLocations: this.monitoringLocations || [],
        selectedMonitoringLocations: this.selectedMonitoringLocations || [],
        searchTerm: this.searchTerm || '',
        showTimescale: this.showTimescale,
        showSearch: this.showSearch,
        showContributingOrganisations: this.showContributingOrganisations,
        showProjects: this.showProjects,
        showLocations: this.showLocations,
        showMonitoringGroups: this.showMonitoringGroups,
        showMonitoringLocations: this.showMonitoringLocations,
        showDownloadObservations: this.showDownloadObservations,
        applyMonitoringDisabled: this.applyMonitoringDisabled,
        applyingMonitoringFilters: this.applyingMonitoringFilters,
        onViewModeChange: (mode: string) => this.runInZone(() => this.viewModeChange.emit(mode)),
        onDateRangeChange: (range: DashboardFilterDateRange) => this.runInZone(() => this.dateRangeChange.emit(range)),
        onContributingOrganisationsChange: (selected: string[]) =>
          this.runInZone(() => this.contributingOrganisationsChange.emit(selected)),
        onProjectsChange: (selected: string[]) => this.runInZone(() => this.projectsChange.emit(selected)),
        onLocationsChange: (selected: string[]) => this.runInZone(() => this.locationsChange.emit(selected)),
        onMonitoringGroupsChange: (selected: string[]) =>
          this.runInZone(() => this.monitoringGroupsChange.emit(selected)),
        onMonitoringLocationsChange: (selected: string[]) =>
          this.runInZone(() => this.monitoringLocationsChange.emit(selected)),
        onSearchTermChange: (term: string) => this.runInZone(() => this.searchTermChange.emit(term)),
        onApplyMonitoringFilters: () => this.runInZone(() => this.applyMonitoringFilters.emit()),
        onDownloadObservations: () => this.runInZone(() => this.downloadObservations.emit()),
      })
    );
  }
}
